import { surfaceWorkspacePresentation } from './workspace-presentation';
import {
  WORKSPACE_COMPATIBILITY_SEGMENT,
  WORKSPACE_FAMILY_CONTRACTS,
} from './workspace-route-contract';

import type {
  AdsDetailSection,
  WorkspaceDestination,
  WorkspacePresentation,
} from './workspace-route-contract';

/**
 * The render half of the route contract. Builders apply exactly ONE `encodeURIComponent` pass to
 * each identifier, and refuse a value that cannot survive as a single path segment — empty, `.`,
 * `..`, or carrying a separator. That keeps the round-trip invariant total: every non-null output
 * parses back to the destination it was built from. See `workspace-route-policy` for the parse half
 * and the full encoding policy.
 */

const UNRENDERABLE_SEGMENTS: ReadonlySet<string> = new Set(['.', '..']);

function encodeSegment(value: string): string | null {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.includes('/') ||
    value.includes('\\') ||
    UNRENDERABLE_SEGMENTS.has(value)
  ) {
    return null;
  }
  return encodeURIComponent(value);
}

/** Dynamic values a destination contributes to its path, in canonical-template placeholder order. */
function destinationParams(destination: WorkspaceDestination): readonly string[] {
  if (destination.family === 'person') {
    return [destination.personId];
  }
  if (destination.family === 'organisation') {
    return [destination.organisationId];
  }
  if (destination.family === 'task-detail') {
    return [destination.taskId];
  }
  if (destination.family === 'document-detail') {
    return [destination.documentId];
  }
  if (destination.family === 'broadcast-builder') {
    return [destination.broadcastId];
  }
  if (destination.family === 'ads-detail') {
    return [destination.section, destination.key];
  }
  if (destination.family === 'composition') {
    return [destination.projectId, destination.compositionId];
  }
  return [];
}

/** Render a destination as a pathname, or `null` when an identifier is not segment-expressible. */
export function workspacePath(
  destination: WorkspaceDestination,
  presentation: WorkspacePresentation,
): string | null {
  const { canonicalSegments } = WORKSPACE_FAMILY_CONTRACTS[destination.family];
  const params = destinationParams(destination);
  const filled: string[] = [];
  let paramIndex = 0;
  for (const segment of canonicalSegments) {
    if (!segment.startsWith(':')) {
      filled.push(segment);
      continue;
    }
    const encoded = encodeSegment(params[paramIndex]);
    paramIndex += 1;
    if (encoded === null) {
      return null;
    }
    filled.push(encoded);
  }
  const prefixed =
    presentation === 'compatibility' ? [WORKSPACE_COMPATIBILITY_SEGMENT, ...filled] : filled;
  return prefixed.length === 0 ? '/' : `/${prefixed.join('/')}`;
}

/**
 * Re-present a CANONICAL href (`/tasks`, `/settings?tab=members`, `/`) in a presentation. The
 * input is trusted canonical text and is not parsed, which is what lets the synthetic tab-route
 * parents (`/ads`, `/knowledge/documents`) and query-carrying hrefs go through the same switch as
 * the typed builders.
 */
export function workspaceHref(
  canonicalHref: string,
  presentation: WorkspacePresentation = surfaceWorkspacePresentation(),
): string {
  if (presentation === 'canonical') {
    return canonicalHref;
  }
  const prefix = `/${WORKSPACE_COMPATIBILITY_SEGMENT}`;
  const isRoot =
    canonicalHref === '/' || canonicalHref.startsWith('/?') || canonicalHref.startsWith('/#');
  return isRoot ? `${prefix}${canonicalHref.slice(1)}` : `${prefix}${canonicalHref}`;
}

/** The identity query keys a destination must carry; empty for every path-addressable family. */
export function workspaceIdentityQuery(
  destination: WorkspaceDestination,
): readonly (readonly [string, string])[] {
  return WORKSPACE_FAMILY_CONTRACTS[destination.family].identityQuery ?? [];
}

export function personPath(personId: string, presentation: WorkspacePresentation): string | null {
  return workspacePath({ family: 'person', personId }, presentation);
}

export function organisationPath(
  organisationId: string,
  presentation: WorkspacePresentation,
): string | null {
  return workspacePath({ family: 'organisation', organisationId }, presentation);
}

export function taskDetailPath(taskId: string, presentation: WorkspacePresentation): string | null {
  return workspacePath({ family: 'task-detail', taskId }, presentation);
}

export function documentDetailPath(
  documentId: string,
  presentation: WorkspacePresentation,
): string | null {
  return workspacePath({ family: 'document-detail', documentId }, presentation);
}

export function adsDetailPath(
  section: AdsDetailSection,
  key: string,
  presentation: WorkspacePresentation,
): string | null {
  return workspacePath({ family: 'ads-detail', section, key }, presentation);
}

export function broadcastBuilderPath(
  broadcastId: string,
  presentation: WorkspacePresentation,
): string | null {
  return workspacePath({ family: 'broadcast-builder', broadcastId }, presentation);
}

/** The connected composition workspace for one composition inside one clip project. */
export function compositionWorkspacePath(
  projectId: string,
  compositionId: string,
  presentation: WorkspacePresentation,
): string | null {
  return workspacePath({ family: 'composition', projectId, compositionId }, presentation);
}
